import type { Tables } from './database.types.js';
import { addDays } from './tasks.js';

// Cycles helpers shared by the app and the MCP server: sprint status from its
// dates, the ideal burndown line, suggested dates for the next sprint, and
// finding a sprint from what Claude typed ("current", "3", "Sprint 3", an id).

export type Sprint = Tables<'sprints'>;
export type TaskDependency = Tables<'task_dependencies'>;
export type SprintStatus = 'planned' | 'active' | 'overdue' | 'closed';

export const SPRINT_STATUSES: readonly { id: SprintStatus; name: string }[] = [
  { id: 'planned', name: 'Planned' },
  { id: 'active', name: 'Active' },
  { id: 'overdue', name: 'Overdue' },
  { id: 'closed', name: 'Closed' },
];

type SprintDates = Pick<Sprint, 'start_date' | 'end_date' | 'closed_at'>;

/** Where a sprint is, on `today` (an ISO date in the studio's time zone). */
export function sprintStatus(sprint: SprintDates, today: string): SprintStatus {
  if (sprint.closed_at) return 'closed';
  if (today < sprint.start_date) return 'planned';
  if (today > sprint.end_date) return 'overdue';
  return 'active';
}

/** Whole days between two ISO dates (b - a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** How many days a sprint runs, counting both ends. */
export function sprintLength(sprint: Pick<Sprint, 'start_date' | 'end_date'>): number {
  return daysBetween(sprint.start_date, sprint.end_date) + 1;
}

/** Days left including today, for an active sprint; 0 once it's over. */
export function sprintDaysLeft(sprint: Pick<Sprint, 'start_date' | 'end_date'>, today: string): number {
  if (today > sprint.end_date) return 0;
  return daysBetween(today < sprint.start_date ? sprint.start_date : today, sprint.end_date) + 1;
}

/** Every date of a sprint, start to end. */
export function sprintDates(sprint: Pick<Sprint, 'start_date' | 'end_date'>): string[] {
  return Array.from({ length: sprintLength(sprint) }, (_, i) => addDays(sprint.start_date, i));
}

/**
 * The ideal burndown: `scope` remaining at the end of the first day falling
 * evenly to zero at the end of the last, one value per sprint day.
 */
export function idealRemaining(sprint: Pick<Sprint, 'start_date' | 'end_date'>, scope: number): number[] {
  const days = sprintLength(sprint);
  if (days <= 1) return [0];
  return Array.from({ length: days }, (_, i) => Math.round(scope * (1 - i / (days - 1)) * 100) / 100);
}

/** Dates for a new sprint: the day after the last one ends (or today), for `lengthDays`. */
export function suggestSprintDates(
  sprints: readonly Pick<Sprint, 'end_date'>[],
  today: string,
  lengthDays = 14,
): { start_date: string; end_date: string } {
  const lastEnd = sprints.reduce<string | null>(
    (max, s) => (max === null || s.end_date > max ? s.end_date : max),
    null,
  );
  const afterLast = lastEnd ? addDays(lastEnd, 1) : today;
  const start = afterLast > today ? afterLast : today;
  return { start_date: start, end_date: addDays(start, lengthDays - 1) };
}

/** The sprint "current" means: the active one, else the oldest still open, else the next planned. */
export function currentSprint<S extends SprintDates>(sprints: readonly S[], today: string): S | undefined {
  const open = sprints.filter((s) => !s.closed_at).sort((a, b) => a.start_date.localeCompare(b.start_date));
  return (
    open.find((s) => sprintStatus(s, today) === 'active') ??
    open.find((s) => sprintStatus(s, today) === 'overdue') ??
    open.find((s) => sprintStatus(s, today) === 'planned')
  );
}

/**
 * A sprint from an id, a number ("3"), a name ("Sprint 3", any case) or
 * "current". Throws with the choices when nothing matches.
 */
export function findSprint<S extends SprintDates & Pick<Sprint, 'id' | 'number' | 'name'>>(
  ref: string,
  sprints: readonly S[],
  today: string,
): S {
  const q = ref.trim().toLowerCase();
  const found =
    q === 'current'
      ? currentSprint(sprints, today)
      : (sprints.find((s) => s.id === q) ??
        (/^#?\d+$/.test(q) ? sprints.find((s) => s.number === Number(q.replace('#', ''))) : undefined) ??
        sprints.find((s) => s.name.toLowerCase() === q));
  if (!found) {
    const names = sprints.map((s) => s.name).join(', ') || 'none yet';
    throw new Error(q === 'current' ? `No open sprint. Sprints: ${names}.` : `No sprint "${ref}". Sprints: ${names}.`);
  }
  return found;
}
